import { type ChangeEvent, useEffect, useRef } from 'react';
import { twMerge } from 'tailwind-merge';
import Dialog from '@mui/material/Dialog';
import InputBase from '@mui/material/InputBase';
import type { DecoratedUnisonProject } from '@witmetrics/utils';
import { isBlank, strip } from '@witmetrics/utils/strings';
import { isEmpty } from '@witmetrics/utils/misc';
import { useAppState } from '@/providers/AppStateProvider';
import { useArray } from '@/hooks/useArray';
import { useString } from '@/hooks/useString';
import SlideTransition from '@/components/SlideTransition';
import Button from '@/components/Button';
import ResultsList from './ResultsList';
import { classes, fetchMatchingProjects, fetchRecentProjects } from './utils';

export type ProjectSearchDialogProps = {
  onClose: () => void;
};

export default function ProjectSearchDialog({
  onClose,
}: ProjectSearchDialogProps) {
  const timestampRef = useRef(Date.now());
  const inputRef = useRef<HTMLInputElement>(null);
  const {
    onApiError,
    onSelectProject,
    recentProjectIds,
    onClearRecentProjects,
  } = useAppState();
  const [search, setSearch] = useString();
  const [suggestions, setSuggestions] = useArray<DecoratedUnisonProject>();
  const [recentProjects, setRecentProjects, { clear: clearRecentProjects }] =
    useArray<DecoratedUnisonProject>();

  useEffect(() => {
    handleFetchRecentProjects();
  }, [recentProjectIds]);

  const handleFetchRecentProjects = async () => {
    if (isEmpty(recentProjectIds)) return clearRecentProjects();
    setRecentProjects(await fetchRecentProjects(recentProjectIds));
  };

  const handleClearRecentProjects = () => {
    onClearRecentProjects();
    if (inputRef.current) inputRef.current.focus();
  };

  const handleSelect = (projectId: number) => {
    onSelectProject(projectId);
    onClose();
  };

  const handleSearch = async (e: ChangeEvent<HTMLInputElement>) => {
    const newSearch = e.target.value;
    const query = strip(newSearch);
    let searchSuggestions: DecoratedUnisonProject[] = [];
    setSearch(newSearch);
    if (query.length > 0) {
      try {
        const results = await fetchMatchingProjects(query);
        // Prevent overwriting newer responses with slower previous ones
        if (results.timestamp > timestampRef.current) {
          searchSuggestions = results.projects;
        } else {
          searchSuggestions = suggestions;
        }
        timestampRef.current = results.timestamp;
      } catch (err) {
        onApiError(err, 'Error finding projects');
      }
    }
    setSuggestions(searchSuggestions);
  };

  const renderSuggestions = () => {
    const showRecent = isBlank(search) && !isEmpty(recentProjects);
    const projects = showRecent ? recentProjects : suggestions;
    return (
      !isEmpty(projects) && (
        <ResultsList
          className={twMerge(
            classes.resultsList,
            showRecent && classes.recentList
          )}
          results={projects}
          onSelect={handleSelect}
        />
      )
    );
  };

  return (
    <Dialog
      open
      keepMounted
      disableRestoreFocus
      maxWidth={false}
      PaperProps={{ className: classes.paper }}
      TransitionComponent={SlideTransition}
      onClose={onClose}>
      <div className={classes.wrapper}>
        <InputBase
          ref={inputRef}
          autoFocus
          className={classes.inputBase}
          autoComplete="off"
          name="projects-search"
          placeholder="Search for cases by customer, name, or description..."
          type="text"
          value={search}
          onChange={handleSearch}
        />
        {isBlank(search) && !isEmpty(recentProjects) && (
          <div className={classes.recentlyViewedWrapper}>
            <div className={classes.recentlyViewed}>Recently viewed</div>
            <Button
              variant="ghost"
              tabIndex={-1 /* Tab straight into projects list */}
              onClick={handleClearRecentProjects}>
              Clear
            </Button>
          </div>
        )}
        {renderSuggestions()}
      </div>
    </Dialog>
  );
}
